/**
 * Application Insights Configuration — Firebase Remote Config Integration
 *
 * Provides dynamic configuration for Application Insights via Firebase Remote Config.
 * Allows changing AI settings without requiring a web deployment.
 * Default values ensure the application works if Firebase Remote Config is unavailable.
 */

/** Shape of the Application Insights dynamic configuration */
export interface AIConfig {
  samplingPercentage: number;
  enableAutoRouteTracking: boolean;
  disableExceptionTracking: boolean;
  disableFetchTracking: boolean;
  disableAjaxTracking: boolean;
  disableTelemetry: boolean;
  maxBatchSizeInBytes: number;
  maxBatchInterval: number;
  loggingLevelTelemetry: number;
}

/** Default Application Insights Configuration */
export const DEFAULT_AI_CONFIG: AIConfig = {
  samplingPercentage: 20,          // Sample 20% of telemetry (80% reduction)
  enableAutoRouteTracking: false,  // Disable auto tracking — we do it manually
  disableExceptionTracking: false, // Keep exception tracking at full rate (CRITICAL)
  disableFetchTracking: true,      // Disable automatic fetch tracking to reduce noise
  disableAjaxTracking: true,       // Disable automatic ajax tracking to reduce noise
  disableTelemetry: false,         // Enable telemetry by default
  maxBatchSizeInBytes: 10000,      // Reduce batch size to minimise data transfer
  maxBatchInterval: 15000,         // Increase batch interval to 15 seconds
  loggingLevelTelemetry: 0         // Default to no logging
};

/** Firebase Remote Config property keys for Application Insights */
export const AI_REMOTE_CONFIG_KEYS = {
  SAMPLING_PERCENTAGE: 'samplingPercentage',
  ENABLE_AUTO_ROUTE_TRACKING: 'enableAutoRouteTracking',
  DISABLE_EXCEPTION_TRACKING: 'disableExceptionTracking',
  DISABLE_FETCH_TRACKING: 'disableFetchTracking',
  DISABLE_AJAX_TRACKING: 'disableAjaxTracking',
  MAX_BATCH_SIZE_IN_BYTES: 'maxBatchSizeInBytes',
  MAX_BATCH_INTERVAL: 'maxBatchInterval',
} as const;

/**
 * Validates Application Insights configuration values.
 * Ensures values are within acceptable ranges and types.
 * Falls back to DEFAULT_AI_CONFIG values for any invalid properties.
 *
 * @param config - Configuration object to validate
 * @returns Validated configuration
 */
export const validateAIConfig = (config: Partial<AIConfig>): AIConfig => {
  const validated: AIConfig = {...DEFAULT_AI_CONFIG};

  // samplingPercentage: number, 1–100
  if (
    typeof config.samplingPercentage === 'number' &&
    config.samplingPercentage >= 1 &&
    config.samplingPercentage <= 100
  ) {
    validated.samplingPercentage = config.samplingPercentage;
  }

  if (typeof config.enableAutoRouteTracking === 'boolean') {
    validated.enableAutoRouteTracking = config.enableAutoRouteTracking;
  }

  // CRITICAL: disableExceptionTracking must NEVER be true in production.
  // Force to false if someone mistakenly sets it to true via Firebase.
  if (typeof config.disableExceptionTracking === 'boolean') {
    if (config.disableExceptionTracking === true) {
      console.warn(
        '[AppInsights] disableExceptionTracking was set to true via Remote Config — ' +
        'this is not allowed. Exception tracking will remain enabled.',
      );
      validated.disableExceptionTracking = false;
    } else {
      validated.disableExceptionTracking = config.disableExceptionTracking;
    }
  }

  if (typeof config.disableFetchTracking === 'boolean') {
    validated.disableFetchTracking = config.disableFetchTracking;
  }

  if (typeof config.disableAjaxTracking === 'boolean') {
    validated.disableAjaxTracking = config.disableAjaxTracking;
  }

  if (typeof config.disableTelemetry === 'boolean') {
    validated.disableTelemetry = config.disableTelemetry;
  }

  // maxBatchSizeInBytes: number, 1000–100000
  if (
    typeof config.maxBatchSizeInBytes === 'number' &&
    config.maxBatchSizeInBytes >= 1000 &&
    config.maxBatchSizeInBytes <= 100000
  ) {
    validated.maxBatchSizeInBytes = config.maxBatchSizeInBytes;
  }

  // maxBatchInterval: number, 1000–60000 ms
  if (
    typeof config.maxBatchInterval === 'number' &&
    config.maxBatchInterval >= 1000 &&
    config.maxBatchInterval <= 60000
  ) {
    validated.maxBatchInterval = config.maxBatchInterval;
  }

  return validated;
};

/**
 * Parses and validates an Application Insights config from a Firebase Remote Config JSON string.
 *
 * @param remoteConfigValue - JSON string from Firebase Remote Config
 * @returns Validated configuration object
 */
export const parseAIRemoteConfig = (remoteConfigValue: string): AIConfig => {
  try {
    if (!remoteConfigValue) {
      console.log('[AppInsights] No remote config found, using defaults');
      return DEFAULT_AI_CONFIG;
    }

    const config: Partial<AIConfig> =
      typeof remoteConfigValue === 'string'
        ? JSON.parse(remoteConfigValue)
        : remoteConfigValue;

    const validatedConfig = validateAIConfig(config);
    console.log('[AppInsights] Config loaded from Firebase Remote Config:', validatedConfig);
    return validatedConfig;
  } catch (error) {
    console.error('[AppInsights] Error parsing remote config:', error);
    return DEFAULT_AI_CONFIG;
  }
};

/**
 * Logs the current Application Insights configuration for debugging.
 *
 * @param config - Current configuration
 * @param source - Source label (e.g. 'Remote Config', 'Default')
 */
export const logAIConfig = (config: AIConfig, source = 'Unknown'): void => {
  console.log(
    `[AppInsights] Configuration (${source}):\n` +
    `  Sampling Percentage    : ${config.samplingPercentage}% (${100 - config.samplingPercentage}% reduction)\n` +
    `  Auto Route Tracking    : ${config.enableAutoRouteTracking ? 'Enabled' : 'Disabled'}\n` +
    `  Exception Tracking     : ${config.disableExceptionTracking ? 'Disabled ⚠️' : 'Enabled ✅'}\n` +
    `  Fetch Tracking         : ${config.disableFetchTracking ? 'Disabled' : 'Enabled'}\n` +
    `  AJAX Tracking          : ${config.disableAjaxTracking ? 'Disabled' : 'Enabled'}\n` +
    `  Telemetry              : ${config.disableTelemetry ? 'Disabled' : 'Enabled'}\n` +
    `  Max Batch Size         : ${config.maxBatchSizeInBytes} bytes\n` +
    `  Max Batch Interval     : ${config.maxBatchInterval}ms`,
  );
};
